import type { Vec2 } from '../../geo/types';
import type { BakedCrowd } from './crowd/crowdPlan';
import type { Ground } from './crowd/walkNet';
import { alongLine, type WalkLine, type OnLine } from './crowd/walkLine';
import { cargoHeadingAt, planCargoRoutes } from './cargoPlan';

export type DraftKind = 'horse-cart' | 'ox-cart' | 'rider' | 'escort';
export interface DraftRoute {
  readonly kind: DraftKind;
  readonly points: readonly Vec2[];
}
export const DRAFT = { trailerM: 3.5, wheelRadiusM: 0.48, count: 6 } as const;

export function draftAt(line: WalkLine, distance: number, out: OnLine): void {
  const wrapped = ((distance % line.length) + line.length) % line.length;
  alongLine(line, wrapped / line.length, out);
  out.heading = cargoHeadingAt(line, wrapped);
}

/** Both bodies follow the curve; their connecting pole must clear the inside of each turn too. */
export function draftFootprint(line: WalkLine, distance: number): Vec2[] {
  const at: OnLine = { x: 0, y: 0, ground: 0, heading: 0 };
  const points: Vec2[] = [];
  const centers: Vec2[] = [];
  for (const offset of [0, -DRAFT.trailerM]) {
    draftAt(line, distance + offset, at);
    const c = Math.cos(at.heading),
      s = Math.sin(at.heading);
    centers.push([at.x + c * 0.7, at.y + s * 0.7]);
    for (const x of [-1.4, -0.7, 0, 0.7, 1.65])
      for (const y of [-1.16, -0.58, 0, 0.58, 1.16])
        points.push([at.x + c * x - s * y, at.y + s * x + c * y]);
  }
  const [a, b] = centers as [Vec2, Vec2];
  for (let step = 0; step <= 8; step++) {
    const t = step / 8;
    points.push([a[0] * t + b[0] * (1 - t), a[1] * t + b[1] * (1 - t)]);
  }
  return points;
}

export function planDraftRoutes(crowd: BakedCrowd, ground: Ground): readonly DraftRoute[] {
  const kinds: readonly DraftKind[] = ['horse-cart', 'ox-cart', 'rider', 'escort'];
  return planCargoRoutes(crowd, ground, {
    count: DRAFT.count,
    clearanceM: 1.8,
    turnRadiusM: 1.8,
    footprint: draftFootprint,
  }).map((points, i) => ({ kind: kinds[i % kinds.length]!, points }));
}
